import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "@/components/LanguageSwitcher";

// Etiquetas estáticas del chrome de las páginas legales (header/footer/labels).
// Se mantienen aquí en lugar de cargar a los locales JSON para minimizar la
// superficie de cambio y agrupar todo el chrome legal en un solo archivo.
const CHROME = {
  es: { docLabel: "DOCUMENTO LEGAL", updatedLabel: "ÚLTIMA ACTUALIZACIÓN", home: "← INICIO", terms: "TÉRMINOS", privacy: "PRIVACIDAD", cookies: "COOKIES", dpa: "DPA", footerTag: "GLOBAL" },
  en: { docLabel: "LEGAL DOCUMENT", updatedLabel: "LAST UPDATED", home: "← HOME", terms: "TERMS", privacy: "PRIVACY", cookies: "COOKIES", dpa: "DPA", footerTag: "GLOBAL" },
  pt: { docLabel: "DOCUMENTO LEGAL", updatedLabel: "ÚLTIMA ATUALIZAÇÃO", home: "← INÍCIO", terms: "TERMOS", privacy: "PRIVACIDADE", cookies: "COOKIES", dpa: "DPA", footerTag: "GLOBAL" },
  fr: { docLabel: "DOCUMENT LÉGAL", updatedLabel: "DERNIÈRE MISE À JOUR", home: "← ACCUEIL", terms: "CONDITIONS", privacy: "CONFIDENTIALITÉ", cookies: "COOKIES", dpa: "DPA", footerTag: "GLOBAL" },
  de: { docLabel: "RECHTLICHES DOKUMENT", updatedLabel: "ZULETZT AKTUALISIERT", home: "← STARTSEITE", terms: "AGB", privacy: "DATENSCHUTZ", cookies: "COOKIES", dpa: "AVV", footerTag: "GLOBAL" },
  it: { docLabel: "DOCUMENTO LEGALE", updatedLabel: "ULTIMO AGGIORNAMENTO", home: "← HOME", terms: "TERMINI", privacy: "PRIVACY", cookies: "COOKIE", dpa: "DPA", footerTag: "GLOBAL" },
};

function chromeFor(lang) {
  const code = (lang || "es").slice(0, 2).toLowerCase();
  return CHROME[code] || CHROME.es;
}

function renderBlock(block, idx) {
  switch (block.type) {
    case "h2":
      return <h2 key={idx}>{block.text}</h2>;
    case "h3":
      return <h3 key={idx}>{block.text}</h3>;
    case "p":
      return <p key={idx} dangerouslySetInnerHTML={{ __html: block.html }} />;
    case "ul":
      return (
        <ul key={idx}>
          {block.items.map((item, j) => (
            <li key={j} dangerouslySetInnerHTML={{ __html: item }} />
          ))}
        </ul>
      );
    default:
      return null;
  }
}

export default function LegalLayout({ title, updatedAt, blocks, children }) {
  const { i18n } = useTranslation();
  const c = chromeFor(i18n.language);

  return (
    <div className="min-h-screen bg-[#F4F4F0] text-[#0A0A0A]">
      {/* Header */}
      <header className="border-b border-[#0A0A0A] bg-[#0A0A0A] text-white">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="legal-home-link">
            <img src="/logo-cia.png" alt="CIA Marketing®" className="w-8 h-8" />
            <span className="font-display text-base leading-none">CIA Marketing®</span>
          </Link>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Link to="/" className="font-mono-mark text-[10px] text-white/70 hover:text-white" data-testid="legal-back-home">
              {c.home}
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-6 md:px-10 py-14 md:py-20" data-testid="legal-page">
        <div className="font-mono-mark text-[10px] text-[#5C5C5C]" data-testid="legal-doc-label">{c.docLabel}</div>
        <h1 className="font-display text-4xl sm:text-5xl mt-3 leading-none" data-testid="legal-title">{title}</h1>
        <div className="font-mono-mark text-[10px] text-[#5C5C5C] mt-4" data-testid="legal-updated-at">
          {c.updatedLabel}: {updatedAt}
        </div>
        <article className="prose prose-neutral max-w-none mt-10 font-mono-data text-sm leading-relaxed [&_h2]:font-display [&_h2]:text-2xl [&_h2]:mt-10 [&_h2]:mb-3 [&_h3]:font-display [&_h3]:text-lg [&_h3]:mt-6 [&_h3]:mb-2 [&_p]:mb-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_li]:mb-1 [&_a]:underline [&_a]:text-[#E53935]">
          {Array.isArray(blocks) && blocks.length > 0 ? blocks.map(renderBlock) : children}
        </article>
      </main>

      {/* Footer */}
      <footer className="border-t border-[#0A0A0A] bg-white mt-20">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-6 font-mono-mark text-[10px] text-[#5C5C5C] flex flex-wrap gap-4 justify-between">
          <span>CIA MARKETING® · 2026 · {c.footerTag}</span>
          <div className="flex gap-4">
            <Link to="/legal/terms" className="hover:text-[#0A0A0A]">{c.terms}</Link>
            <Link to="/legal/privacy" className="hover:text-[#0A0A0A]">{c.privacy}</Link>
            <Link to="/legal/cookies" className="hover:text-[#0A0A0A]">{c.cookies}</Link>
            <Link to="/legal/dpa" className="hover:text-[#0A0A0A]">{c.dpa}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
